// PatientCommunicationsTab.js - Flik "Utskick" i patientvyn (Spår L).
// Allt som skickats till patienten: kallelser, bekräftelser, påminnelser, SMS, e-post, formulär.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import CommunicationList from '../Communications/CommunicationList';
import {
  getPatientCommunications, CHANNEL_LABELS, CATEGORY_LABELS,
} from '../Communications/communicationsApi';

const PAGE_SIZE = 50;

const PatientCommunicationsTab = ({ patientId, refreshKey }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [channel, setChannel] = useState('');
  const [category, setCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  // Ökas vid varje omladdning; ett "Visa fler"-svar från en äldre laddning kastas
  const loadGeneration = useRef(0);

  const buildParams = useCallback((offset) => {
    const params = { limit: PAGE_SIZE, offset };
    if (channel) params.channel = channel;
    if (category) params.category = category;
    return params;
  }, [channel, category]);

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    loadGeneration.current += 1;
    setLoading(true);
    setError(null);
    getPatientCommunications(patientId, buildParams(0))
      .then(data => {
        if (cancelled) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Error loading communications:', err);
        setError('Kunde inte hämta utskick');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [patientId, buildParams, refreshKey]);

  const loadMore = async () => {
    const generation = loadGeneration.current;
    try {
      setLoadingMore(true);
      const data = await getPatientCommunications(patientId, buildParams(items.length));
      if (generation !== loadGeneration.current) return; // filtret ändrades under tiden
      // Offset-paginering: nya utskick under tiden kan förskjuta sidan → dedupa på id
      setItems(prev => {
        const seen = new Set(prev.map(i => i.comm_id));
        return [...prev, ...(data.items || []).filter(i => !seen.has(i.comm_id))];
      });
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Error loading more communications:', err);
      setError('Kunde inte hämta fler utskick');
    } finally {
      setLoadingMore(false);
    }
  };

  const filtered = channel || category;

  return (
    <div className="tab-content payments-tab">
      <div className="payments-header">
        <h4>Utskick</h4>
        <div className="comm-filters" style={{ display: 'flex', gap: '8px' }}>
          <select value={channel} onChange={e => setChannel(e.target.value)} aria-label="Kanal">
            <option value="">Alla kanaler</option>
            {Object.entries(CHANNEL_LABELS).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <select value={category} onChange={e => setCategory(e.target.value)} aria-label="Typ">
            <option value="">Alla typer</option>
            {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="patient-error">{error}</div>}

      {loading ? (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i> Laddar utskick...
        </div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-paper-plane"></i>
          <p>{filtered ? 'Inga utskick matchar filtret' : 'Inga utskick registrerade'}</p>
        </div>
      ) : (
        <>
          <CommunicationList items={items} />
          {items.length < total && (
            <div style={{ textAlign: 'center', marginTop: '12px' }}>
              <button className="btn btn-secondary btn-sm" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? 'Laddar...' : `Visa fler (${total - items.length} till)`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PatientCommunicationsTab;
